import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Plus, Calendar, Eye, Clock } from 'lucide-react';
import { PractitionerClient } from '@/types/practitioner';
import { format } from 'date-fns';

interface EnhancedClientCardProps {
  client: PractitionerClient;
  sessionCount: number;
  lastSessionDate?: Date;
  activeLinks: number;
  pendingLinks: number;
  onEdit: (client: PractitionerClient) => void;
  onDelete: (client: PractitionerClient) => void;
  onCreateSession: (client: PractitionerClient) => void;
}

export function EnhancedClientCard({
  client,
  sessionCount,
  lastSessionDate,
  activeLinks,
  pendingLinks,
  onEdit,
  onDelete,
  onCreateSession,
}: EnhancedClientCardProps) {
  return (
    <Card className="meditation-card h-full bg-blue-50/30 dark:bg-blue-950/30 border-blue-200/50 dark:border-blue-800/50 hover:bg-blue-50/50 dark:hover:bg-blue-950/50 transition-colors">
      <CardHeader className="pb-3 bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/50 rounded-t-lg">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg text-blue-900 dark:text-blue-100">
              {client.first_name}{client.last_name ? ' ' + client.last_name : ''}
            </CardTitle>
            {client.email && (
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">{client.email}</p>
            )}
            {client.phone && (
              <p className="text-sm text-blue-700 dark:text-blue-300">{client.phone}</p>
            )}
          </div>
          <div className="flex flex-col items-end space-y-1">
            {sessionCount > 0 && (
              <Badge variant="secondary" className="text-xs bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                <Calendar className="w-3 h-3 mr-1" />
                {sessionCount} sessions
              </Badge>
            )}
            {activeLinks > 0 && (
              <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-700 text-white">
                <Eye className="w-3 h-3 mr-1" />
                {activeLinks} active
              </Badge>
            )}
            {pendingLinks > 0 && (
              <Badge variant="outline" className="text-xs border-orange-300 text-orange-700 dark:border-orange-600 dark:text-orange-400">
                <Clock className="w-3 h-3 mr-1" />
                {pendingLinks} pending
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          {client.notes && (
            <p className="text-sm text-blue-700 dark:text-blue-300 line-clamp-2">{client.notes}</p>
          )}
          
          {lastSessionDate && (
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Last session: {format(lastSessionDate, 'MMM d, yyyy')}
            </p>
          )}
          
          <div className="flex items-center justify-between pt-2 border-t border-blue-200/50 dark:border-blue-800/50">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(client)}
                className="h-8 border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"
              >
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(client)}
                className="h-8 text-destructive hover:text-destructive hover:bg-red-50 dark:hover:bg-red-950"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() => onCreateSession(client)}
              className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-3 h-3 mr-1" />
              Session
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}